class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', async (event) => {
      event.preventDefault();
      const row = this.closest('.cart-item');
      const groupId   = row?.dataset.groupId || null;
      const groupType = row?.dataset.groupType || 'base';
      const bundleTs  = row?.dataset.bundleTs || null;
      const index     = Number(this.dataset.index);
      const shippingBegin = Number(this.dataset.shippingBegin) || 0;
      const shippingEnd = Number(this.dataset.shippingEnd) || 0;
      const backorderDate = this.dataset.backorderDate || '';
      const baseItemKey = this.dataset.baseItemKey || null;
      const ts = this.dataset.ts || null;
      const shippingTime = formatShippingTime(shippingBegin, shippingEnd, backorderDate);

      // Update base product Shipping Time property
      if (baseItemKey) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: baseItemKey,
            properties: { 'Shipping Time': shippingTime, '_ts': ts, '_type': 'base' }
          })
        });
      }

      const cartItems =
        this.closest('cart-items') || this.closest('cart-drawer-items');
        cartItems.updateQuantity(index, 0, undefined, groupId, groupType === 'base', bundleTs);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

async function getCart() {
  const r = await fetch('/cart.js');
  return r.json();
}

async function removeLineByKey(lineKey) {
  const fd = new FormData();
  fd.append('id', lineKey);
  fd.append('quantity', 0);
  await fetch('/cart/change.js', { method: 'POST', body: fd });
}

/**
 * Remove all upsell/bundle lines that match a timestamp (_ts).
 * This ensures only upsells from the specific bundle instance are removed.
 */
async function removeUpsellsForTimestamp(bundleTs) {
  if (!bundleTs) return;
  const cart = await getCart();
  const victims = cart.items.filter((li) => {
    const p = li.properties || {};
    return p._ts === bundleTs && (p._type === 'bundle' || p._type === 'upsell' || p._type === 'crosssell' || p['Bundled With']);
  });
  for (const li of victims) {
    await removeLineByKey(li.key);
  }
}

/**
 * Remove all upsell/bundle lines that match a base handle (groupId).
 * Fallback for when _ts is not available.
 */
async function removeUpsellsForGroupId(groupId) {
  if (!groupId) return;
  const cart = await getCart();
  const victims = cart.items.filter((li) => {
    const p = li.properties || {};
    return p._grouped_id === groupId && (p._type === 'bundle' || p._type === 'upsell' || p._type === 'crosssell' || p['Bundled With']);
  });
  for (const li of victims) {
    await removeLineByKey(li.key);
  }
}

/** Optional: clean up any upsells left without a base (covers qty edits, etc.) */
function syncCartDrawerEmptyState(cartDrawer, isEmpty) {
  if (!cartDrawer) return;
  cartDrawer.classList.toggle('is-empty', isEmpty);
  if (isEmpty) {
    cartDrawer.classList.remove('drawer__close');
  }
}

async function purgeOrphanBundles() {
  const cart = await getCart();
  
  // Collect all _ts values from base products
  const baseTimestamps = new Set(
    cart.items
      .filter((li) => {
        const p = li.properties || {};
        return p._ts && p._type === 'base';
      })
      .map((li) => li.properties._ts)
  );

  // Find orphan bundle items (have _ts but no matching base)
  const orphans = cart.items.filter((li) => {
    const p = li.properties || {};
    return p._ts && (p._type === 'bundle' || p._type === 'upsell' || p._type === 'crosssell') && !baseTimestamps.has(p._ts);
  });

  for (const li of orphans) {
    await removeLineByKey(li.key);
  }
}

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') ||
      document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') return;
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name')
    );
  }

  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=main-cart-items`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceQty = html.querySelector('cart-items');
        if (sourceQty) this.innerHTML = sourceQty.innerHTML;
      })
      .catch((e) => console.error(e));
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      { id: 'cart-icon-bubble',      section: 'cart-icon-bubble',      selector: '.shopify-section' },
      { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  /**
   * Refresh all cart sections and return the live cart object.
   */
  async refreshAllSections() {
    const sections = this.getSectionsToRender();
    const keys = sections.map((s) => s.section).join(',');
    const res = await fetch(`${routes.cart_url}?sections=${keys}`);
    const htmlMap = await res.json(); // { [sectionKey]: "<html...>" }

    // Replace section HTML
    sections.forEach((section) => {
      const container = document.getElementById(section.id);
      if (!container) return;
      const target = container.querySelector(section.selector) || container;
      const html = htmlMap[section.section];
      if (html) target.innerHTML = this.getSectionInnerHTML(html, section.selector);
    });

    // Toggle empty states from live cart
    const cart = await getCart();
    this.classList.toggle('is-empty', cart.item_count === 0);
    const cartDrawerWrapper = document.querySelector('cart-drawer');
    const cartFooter = document.getElementById('main-cart-footer');
    if (cartFooter) cartFooter.classList.toggle('is-empty', cart.item_count === 0);
    syncCartDrawerEmptyState(cartDrawerWrapper, cart.item_count === 0);

    return cart;
  }

  /**
   * Safely trap focus in the drawer, with robust fallbacks.
   * Prevents "DOMTokenList.add('')" errors inside the a11y lib.
   */
  trapFocusSafely({ name, line, isEmpty }) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    // Preferred: the control that changed (by name) within the specific line
    let target = null;
    if (name) {
      const lineItem =
        document.getElementById(`CartDrawer-Item-${line}`) ||
        document.getElementById(`CartItem-${line}`);
      target = lineItem?.querySelector?.(`[name="${name}"]`) || null;
    }

    // If cart is empty, focus the empty state link if present
    if (!target && isEmpty) {
      const emptyWrap = drawer.querySelector('.drawer__inner-empty');
      const anchor = drawer.querySelector('.drawer__inner-empty a, .drawer__inner-empty [href]');
      const closeBtn = drawer.querySelector('.drawer__inner-empty .drawer__close');
      if (emptyWrap && anchor) {
        try { trapFocus(emptyWrap, { elementToFocus: anchor }); } catch (_) {}
        return;
      }
      if (emptyWrap && closeBtn) {
        try { trapFocus(emptyWrap, { elementToFocus: closeBtn }); } catch (_) {}
        return;
      }
    }

    // General fallback: product title or first focusable
    if (!target) {
      target =
        drawer.querySelector('.cart-item__name') ||
        drawer.querySelector('button,[href],input,select,textarea,[tabindex]:not([tabindex="-1"])');
    }

    if (target) {
      try { trapFocus(drawer, { elementToFocus: target }); } catch (_) {}
    }
  }

  /**
   * Support cascade removal by accepting groupId/isBase.
   * Other callers (qty changes) don’t pass them—defaults keep them working.
   */
  async updateQuantity(line, quantity, name, groupId = null, isBase = false, bundleTs = null) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((s) => s.section),
      sections_url: window.location.pathname,
    });

    try {
      const response  = await fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body });
      const stateText = await response.text();
      const parsedState = JSON.parse(stateText);

      const quantityElement =
        document.getElementById(`Quantity-${line}`) ||
        document.getElementById(`Drawer-quantity-${line}`);

      if (parsedState.errors) {
        if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
        this.updateLiveRegions(line, parsedState.errors);
        return;
      }

      // Cascade path: base removed -> remove linked upsells by _ts, purge orphans, refresh all sections once
      if (isBase && Number(quantity) === 0 && (bundleTs || groupId)) {
        // Prefer _ts for finding linked upsells, fallback to groupId
        if (bundleTs) {
          await removeUpsellsForTimestamp(bundleTs);
        }
        await purgeOrphanBundles();
        const cart = await this.refreshAllSections();
        // safe focus after full refresh
        this.trapFocusSafely({ name, line, isEmpty: cart.item_count === 0 });
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
        return; // done
      }

      // Normal render path using the server response sections
      this.classList.toggle('is-empty', parsedState.item_count === 0);
      const cartDrawerWrapper = document.querySelector('cart-drawer');
      const cartFooter = document.getElementById('main-cart-footer');
      if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
      syncCartDrawerEmptyState(cartDrawerWrapper, parsedState.item_count === 0);

      this.getSectionsToRender().forEach((section) => {
        const el =
          document.getElementById(section.id).querySelector(section.selector) ||
          document.getElementById(section.id);
        el.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
      });

      // Safe focus (replaces all raw trapFocus calls)
      this.trapFocusSafely({ name, line, isEmpty: parsedState.item_count === 0 });

      // Safety: tidy up if something else created orphans (e.g., qty edits)
      await purgeOrphanBundles();

      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
    } catch (e) {
      console.error(e);
    } finally {
      this.disableLoading(line);
    }
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message;

    this.lineItemStatusElement?.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') ||
      document.getElementById('CartDrawer-LiveRegionText');
    if (!cartStatus) return;

    cartStatus.setAttribute('aria-hidden', false);
    setTimeout(() => { cartStatus.setAttribute('aria-hidden', true); }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems =
      document.getElementById('main-cart-items') ||
      document.getElementById('CartDrawer-CartItems');
    mainCartItems?.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);
    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement?.blur();
    this.lineItemStatusElement?.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems =
      document.getElementById('main-cart-items') ||
      document.getElementById('CartDrawer-CartItems');
    mainCartItems?.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);
    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'change',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
